import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { config } from '../lib/config';
import { supabase } from '../lib/supabaseClient';
import { demoStore } from '../features/demo/store';
import type { CountryRow, WeaponRow, WikiHistoryRow, WikiImage } from '../types/db';

export const useCountries = () =>
  useQuery({
    queryKey: ['countries'],
    queryFn: async () => {
      if (config.e2e) return demoStore.countries();
      const { data, error } = await supabase.from('countries').select('*').order('name');
      if (error) throw error;
      return data as CountryRow[];
    },
  });
export const useCountry = (id: string) =>
  useQuery({
    queryKey: ['country', id],
    queryFn: async () => {
      if (config.e2e) return demoStore.countries().find((x) => x.id === id) ?? null;
      const { data, error } = await supabase.from('countries').select('*').eq('id', id).single();
      if (error) throw error;
      return data as CountryRow;
    },
  });
export const useWeapons = (country: string) =>
  useQuery({
    queryKey: ['weapons', country],
    queryFn: async () => {
      if (config.e2e) return demoStore.weapons().filter((x) => x.country.toLowerCase() === country);
      const { data, error } = await supabase
        .from('weapons')
        .select('*')
        .ilike('country', country)
        .order('name');
      if (error) throw error;
      return data as WeaponRow[];
    },
  });
export const useHistory = (weaponId?: string) =>
  useQuery({
    queryKey: ['wiki-history', weaponId],
    enabled: Boolean(weaponId),
    queryFn: async () => {
      if (config.e2e)
        return demoStore
          .history()
          .filter((x) => x.weapon_id === weaponId)
          .reverse();
      const { data, error } = await supabase
        .from('wiki_history')
        .select('*')
        .eq('weapon_id', weaponId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as WikiHistoryRow[];
    },
  });
export function useWikiMutations(userId: string) {
  const client = useQueryClient();
  const updateCountry = useMutation({
    mutationFn: async ({
      id,
      ...patch
    }: Pick<
      CountryRow,
      | 'id'
      | 'summary'
      | 'government'
      | 'geography'
      | 'doctrine'
      | 'industry'
      | 'history'
      | 'alliances'
      | 'forces'
    >) => {
      if (config.e2e) return demoStore.updateCountry(id, patch, userId);
      const before = (await supabase.from('countries').select('*').eq('id', id).single())
        .data as CountryRow;
      const { data, error } = await supabase
        .from('countries')
        .update({ ...patch, updated_by: userId, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      const history = await supabase
        .from('wiki_history')
        .insert({ country_id: id, editor: userId, change: { before, after: data } });
      if (history.error) throw history.error;
      return data as CountryRow;
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: ['country'] }),
  });
  const saveWeapon = useMutation({
    mutationFn: async (weapon: WeaponRow) => {
      if (config.e2e) return demoStore.saveWeapon(weapon, userId);
      const before = weapon.id
        ? ((await supabase.from('weapons').select('*').eq('id', weapon.id).maybeSingle())
            .data as WeaponRow | null)
        : null;
      const payload = {
        country: weapon.country,
        category: weapon.category,
        name: weapon.name,
        type: weapon.type,
        specs: weapon.specs,
        description: weapon.description,
        images: weapon.images,
        updated_by: userId,
        version: before ? before.version + 1 : 1,
        updated_at: new Date().toISOString(),
        ...(!before ? { created_by: userId } : {}),
      };
      const result = before
        ? await supabase.from('weapons').update(payload).eq('id', weapon.id).select().single()
        : await supabase.from('weapons').insert(payload).select().single();
      if (result.error) throw result.error;
      const history = await supabase.from('wiki_history').insert({
        weapon_id: result.data.id,
        editor: userId,
        change: { before, after: result.data },
      });
      if (history.error) throw history.error;
      return result.data as WeaponRow;
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: ['weapons'] }),
  });
  const upload = async (file: File, caption: string): Promise<WikiImage> => {
    if (config.e2e) return { url: URL.createObjectURL(file), caption, uploader: userId };
    const path = `${userId}/${crypto.randomUUID()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '-')}`;
    const { error } = await supabase.storage.from('wiki-images').upload(path, file);
    if (error) throw error;
    const { data } = supabase.storage.from('wiki-images').getPublicUrl(path);
    return { url: data.publicUrl, caption, uploader: userId };
  };
  return { updateCountry, saveWeapon, upload };
}
